import OpenFin from '@openfin/core';
import {
    buildLogger,
    isQueryParameterPresent,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { AuthenticateMessagePayload } from 'embedded/api/in/messages/AuthenticateMessagePayload';
import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import { SymbologyValues } from '../store/appSettings/AppSettingsState';
import { SetPositionsMessagePayload } from 'embedded/api/in/messages/SetPositionsMessagePayload';
import { OutMessagePayload } from 'embedded/api/out/OutMessage';
import { ReloadRequiredMessagePayload } from 'embedded/api/out/messages/ReloadRequiredMessage';
import { ReauthenticationRequiredMessagePayload } from 'embedded/api/out/messages/ReauthenticationRequiredMessage';
import { LayoutLoadedMessagePayload } from 'embedded/api/out/messages/LayoutLoadedMessage';
import { SetOrderIdsMessagePayload } from 'embedded/api/in/messages/SetOrderIdsMessagePayload';
import { SetPortfolioMessagePayload } from 'embedded/api/in/messages/SetPortfolioMessagePayload';
import {
    ConnectionMessagePayload,
    InFunctionNames,
    OutFunctionNames,
} from './types';
import { MarketDataService } from 'marketData/MarketDataService';
import { OpenFinApiExternalMarketDataStrategy } from './OpenFinApiExternalMarketDataStrategy';
import { SidecarMarketDataStrategy } from './sidecar/SidecarMarketDataStrategy';
import { SymbolsService } from 'symbols/SymbolsService';
import { ActionsIntervalDispatcherFactory } from 'store/ActionsDispatcher';

export const embeddedApiIdentity = {
    uuid: 'openFin',
    name: 'openFin Embedded API proxy',
};

type Options = {
    actionsIntervalDispatcherFactory: ActionsIntervalDispatcherFactory;
    embeddedApiService: EmbeddedApiService;
    loggingService: LoggingService;
    marketDataService: MarketDataService;
    symbolsService: SymbolsService;
};

/*
 * OpenFin provider for Indigo
 */
export async function bootstrapOpenFin(options: Options) {
    let identity: OpenFin.ClientIdentity | undefined = undefined;

    const {
        embeddedApiService,
        loggingService,
        actionsIntervalDispatcherFactory,
    } = options;

    const channelName = indigoChannelName(fin.me.name);

    const logger = buildLogger('bootstrapOpenFin', loggingService);

    logger.debug('Bootstrapping openFin ', { channelName, options });

    const clientChannel: OpenFin.ChannelProvider =
        await fin.InterApplicationBus.Channel.create(channelName);

    registerInMethods(clientChannel, embeddedApiService);

    clientChannel.onConnection(async (receivedIdentity, connectionMessage) => {
        logger.info('Application connecting to channel:', {
            channelName,
            identity: receivedIdentity,
            connectionMessage,
        });
        identity = receivedIdentity;

        const payload = connectionMessage as ConnectionMessagePayload;
        if (
            payload?.application &&
            payload?.applicationVersion &&
            payload?.token
        ) {
            await embeddedApiService.receiveMessage('AUTHENTICATE', payload);
        }

        if (payload.stylesheetUrl) {
            embeddedApiService.setStylesheetUrl(
                connectionMessage.stylesheetUrl
            );
        }
        if (payload.hideWindowButtons) {
            embeddedApiService.hideWindowButtons();
        }

        if (payload.hostBorderPx) {
            embeddedApiService.setHostBorderPx(connectionMessage.hostBorderPx);
        }

        await registerOutMethods(clientChannel, embeddedApiService, identity);

        const useInteropStrategy =
            connectionMessage.marketDataInterop ||
            isQueryParameterPresent('mktDataInterop', ['1', 'true']);

        if (useInteropStrategy) {
            logger.info(
                'Using OpenFinApiExternalMarketDataStrategy for market data'
            );
            const strategy = new OpenFinApiExternalMarketDataStrategy({
                clientChannel,
                identity,
                loggingService,
            });
            options.marketDataService.setStrategy(strategy);
        }

        const useSidecar =
            connectionMessage.sidecar ||
            isQueryParameterPresent('mktDataSidecar', ['1', 'true']);
        if (useSidecar) {
            logger.info('Using SidecarMarketDataStrategy for market data');
            const strategy = new SidecarMarketDataStrategy({
                loggingService,
                embeddedApiService,
                actionsIntervalDispatcherFactory,
            });
            options.marketDataService.setStrategy(strategy);
        }
    });

    clientChannel.onDisconnection(async (identity) => {
        logger.info('Client channel disconnected', identity);
        await unsubscribeOutMethods(embeddedApiService);
    });
}

function indigoChannelName(windowName: string) {
    return `Indigo/${windowName}/${fin.me.uuid}`;
}

function registerInMethods(
    clientChannel: OpenFin.ChannelProvider,
    embeddedApiService: EmbeddedApiService
) {
    clientChannel.register(
        InFunctionNames.Authenticate,
        async (unknownPayload: unknown, _identity) => {
            const payload = unknownPayload as AuthenticateMessagePayload;
            await embeddedApiService.receiveMessage('AUTHENTICATE', payload);
        }
    );

    clientChannel.register(
        InFunctionNames.SetInstrument,
        async (unknownPayload: unknown, _identity) => {
            const payload = unknownPayload as {
                symbology: SymbologyValues;
                symbol: string;
                side?: 'B' | 'S' | 'SS';
                qty?: number;
            };
            const apiPayload: SetPositionsMessagePayload = {
                symbology: payload.symbology,
                positions: [
                    {
                        symbol: payload.symbol,
                        side: payload.side,
                        qty: payload.qty,
                    },
                ],
            };
            return embeddedApiService.receiveMessage(
                'SET_POSITIONS',
                apiPayload
            );
        }
    );

    clientChannel.register(
        InFunctionNames.SetOrderIds,
        async (unknownPayload: unknown, _identity) => {
            const payload = unknownPayload as SetOrderIdsMessagePayload;
            return embeddedApiService.receiveMessage('SET_ORDER_IDS', payload);
        }
    );

    clientChannel.register(
        InFunctionNames.SetPortfolio,
        async (unknownPayload: unknown, _identity) => {
            const payload = unknownPayload as SetPortfolioMessagePayload;
            return embeddedApiService.receiveMessage('SET_PORTFOLIO', payload);
        }
    );

    clientChannel.register(
        InFunctionNames.SetPositions,
        async (unknownPayload: unknown, _identity) => {
            const payload = unknownPayload as SetPositionsMessagePayload;
            return embeddedApiService.receiveMessage('SET_POSITIONS', payload);
        }
    );

    clientChannel.register(
        InFunctionNames.Logout,
        async (unknownPayload: unknown, _identity) => {
            const payload = unknownPayload as AuthenticateMessagePayload;
            await embeddedApiService.receiveMessage('LOGOUT', payload);
        }
    );
}

async function registerOutMethods(
    clientChannel: OpenFin.ChannelProvider,
    embeddedApiService: EmbeddedApiService,
    identity: OpenFin.Identity
) {
    await embeddedApiService.subscribe<ReloadRequiredMessagePayload>(
        embeddedApiIdentity,
        'RELOAD_REQUIRED',
        (payload) =>
            clientChannel?.dispatch(
                identity!,
                OutFunctionNames.ReloadRequired,
                payload
            )
    );

    await embeddedApiService.subscribe<ReauthenticationRequiredMessagePayload>(
        embeddedApiIdentity,
        'REAUTHENTICATION_REQUIRED',
        (payload) =>
            clientChannel?.dispatch(
                identity!,
                OutFunctionNames.ReauthenticationRequired,
                payload
            )
    );

    await embeddedApiService.subscribe<OutMessagePayload>(
        embeddedApiIdentity,
        'AUTHENTICATION_STATUS_CHANGED',
        (payload) =>
            clientChannel?.dispatch(
                identity!,
                OutFunctionNames.AuthenticationStatusChanged,
                payload
            )
    );

    await embeddedApiService.subscribe<LayoutLoadedMessagePayload>(
        embeddedApiIdentity,
        'LAYOUT_LOADED',
        (payload) =>
            clientChannel?.dispatch(
                identity!,
                OutFunctionNames.LayoutLoaded,
                payload
            )
    );
}

async function unsubscribeOutMethods(embeddedApiService: EmbeddedApiService) {
    await embeddedApiService.unsubscribe(
        embeddedApiIdentity,
        'RELOAD_REQUIRED'
    );
    await embeddedApiService.unsubscribe(
        embeddedApiIdentity,
        'REAUTHENTICATION_REQUIRED'
    );
    await embeddedApiService.unsubscribe(
        embeddedApiIdentity,
        'AUTHENTICATION_STATUS_CHANGED'
    );
    await embeddedApiService.unsubscribe(embeddedApiIdentity, 'LAYOUT_LOADED');
}
